import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dependentsList", "addButton", "dependentRow", "rowTemplate", "nameInput", "ssnInput", "dobInput", "relationshipSelect"]
  static outlets = ["tax-organizer--scroll-position", "tax-organizer--progress"]

  static values = {
    maxDependents: { type: Number, default: 10 },
    createUrl: String,
    updateUrl: String,
    destroyUrl: String,
    questionId: String
  }

  connect() {
    this.nextIndex = 0
    this.updateAddButtonState()
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this.savingRows = new Set() // Track which rows are currently saving
    this.lastSavedValues = new Map() // Track last saved values per row
  }

  // Get current values for a specific row
  getCurrentValues(row) {
    const nameInput = this.nameInputTargets.find(target => row.contains(target))
    const ssnInput = this.ssnInputTargets.find(target => row.contains(target))
    const dobInput = this.dobInputTargets.find(target => row.contains(target))
    const relationshipSelect = this.relationshipSelectTargets.find(target => row.contains(target))

    return {
      name: nameInput ? nameInput.value.trim() : '',
      ssn: ssnInput ? ssnInput.value.trim() : '',
      dateOfBirth: dobInput ? dobInput.value.trim() : '',
      relationship: relationshipSelect ? relationshipSelect.value.trim() : ''
    }
  }

  // Check if row has changes since last save
  hasChanges(rowId, currentValues) {
    const lastSaved = this.lastSavedValues.get(rowId)
    if (!lastSaved) return true // No saved values yet, consider it changed

    return currentValues.name !== lastSaved.name ||
           currentValues.ssn !== lastSaved.ssn ||
           currentValues.dateOfBirth !== lastSaved.dateOfBirth ||
           currentValues.relationship !== lastSaved.relationship
  }

  // Add a new dependent row by cloning the template (client-side only, no server request yet)
  addDependent(event) {
    event.preventDefault()

    const visibleCount = this.dependentRowTargets.filter(row => row.style.display !== 'none').length
    if (visibleCount >= this.maxDependentsValue) {
      return
    }

    // Clone the template
    const template = this.rowTemplateTarget
    const clone = template.content.cloneNode(true)

    // Update the input names with unique index
    const timestamp = Date.now()
    const index = this.nextIndex++
    const uniqueId = `new_${timestamp}_${index}`

    // Update all input/select/textarea names in the cloned template
    clone.querySelectorAll('[data-name-template]').forEach(el => {
      const nameTemplate = el.dataset.nameTemplate
      el.name = nameTemplate.replace('INDEX', uniqueId)
      el.removeAttribute('data-name-template')
    })

    // Set a data attribute so we know this is a new (unsaved) row
    const rowDiv = clone.querySelector('.dependent-row')
    if (rowDiv) {
      rowDiv.id = `dependent_temp_${uniqueId}` // Set temporary ID for turbo stream replacement
      rowDiv.dataset.isNew = 'true'
      rowDiv.dataset.tempId = uniqueId
    }

    // Append to list
    this.dependentsListTarget.appendChild(clone)

    this.updateAddButtonState()

    // Focus on the name field of the new row
    const newRow = this.dependentRowTargets[this.dependentRowTargets.length - 1]
    const nameInput = this.nameInputTargets.find(target => newRow.contains(target))
    if (nameInput) {
      nameInput.focus()
    }

    // Remember scroll position
    if (this.hasTaxOrganizerScrollPositionOutlet) {
      this.taxOrganizerScrollPositionOutlet.rememberFromElement(this.element)
    }
  }

  // Remove a dependent row
  async removeDependent(event) {
    event.preventDefault()

    const row = event.currentTarget.closest('.dependent-row')
    const dependentId = row.dataset.dependentId
    const isNew = row.dataset.isNew === 'true'

    // If this is a new (unsaved) row, just remove it from DOM
    if (isNew || !dependentId) {
      row.remove()
      this.updateAddButtonState()
      return
    }

    // For persisted records, confirm and make DELETE request
    if (!confirm('Are you sure you want to remove this dependent?')) {
      return
    }

    // Remember scroll position
    if (this.hasTaxOrganizerScrollPositionOutlet) {
      this.taxOrganizerScrollPositionOutlet.rememberFromElement(row)
    }

    // Make DELETE request
    const url = this.destroyUrlValue.replace(':id', dependentId)
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': this.csrfToken,
        'Accept': 'text/vnd.turbo-stream.html'
      }
    })

    if (response.ok) {
      // Manually process Turbo Stream response
      const html = await response.text()
      Turbo.renderStreamMessage(html)
      this.updateAddButtonState()
    }
  }

  // Update add button state based on current count
  updateAddButtonState() {
    if (!this.hasAddButtonTarget) return

    const visibleCount = this.dependentRowTargets.filter(row => row.style.display !== 'none').length

    if (visibleCount >= this.maxDependentsValue) {
      this.addButtonTarget.disabled = true
      this.addButtonTarget.classList.add('disabled')
    } else {
      this.addButtonTarget.disabled = false
      this.addButtonTarget.classList.remove('disabled')
    }
  }

  // Auto-save on blur (like filer_info)
  async autoSave(event) {
    // Find which row this event came from using targets
    const row = this.dependentRowTargets.find(row => row.contains(event.target))
    if (!row) return

    // Get unique identifier for this row (tempId for new rows, dependentId for saved rows)
    const rowId = row.dataset.tempId || row.dataset.dependentId

    // Check if values have changed
    const currentValues = this.getCurrentValues(row)
    if (!this.hasChanges(rowId, currentValues)) {
      return // No changes, skip save
    }

    // Prevent multiple simultaneous saves for the same row
    if (this.savingRows.has(rowId)) return

    try {
      this.savingRows.add(rowId)

      // Find the inputs within this specific row using targets
      const nameInput = this.nameInputTargets.find(target => row.contains(target))
      const ssnInput = this.ssnInputTargets.find(target => row.contains(target))
      const dobInput = this.dobInputTargets.find(target => row.contains(target))
      const relationshipSelect = this.relationshipSelectTargets.find(target => row.contains(target))

      if (!nameInput) return

      // Remember scroll position
      if (this.hasTaxOrganizerScrollPositionOutlet) {
        this.taxOrganizerScrollPositionOutlet.rememberFromElement(row)
      }

      // Show spinner on question badge
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.showQuestionSaving(this.questionIdValue)
      }

      // Determine if this is a new or existing record
      const dependentId = row.dataset.dependentId
      const tempId = row.dataset.tempId
      const isNew = row.dataset.isNew === 'true' || !dependentId

      // Build form data
      const formData = new FormData()
      formData.append('dependent[name]', nameInput.value)
      formData.append('dependent[ssn]', ssnInput ? ssnInput.value : '')
      formData.append('dependent[date_of_birth]', dobInput ? dobInput.value : '')
      formData.append('dependent[relationship]', relationshipSelect ? relationshipSelect.value : '')

      // Pass temp ID for new records so server knows what element to replace
      if (isNew && tempId) {
        formData.append('temp_id', tempId)
      }

      const url = isNew ? this.createUrlValue : this.updateUrlValue.replace(':id', dependentId)
      const method = isNew ? 'POST' : 'PATCH'

      const response = await fetch(url, {
        method: method,
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: formData
      })

      // Manually process Turbo Stream response (fetch doesn't do this automatically)
      if (response.ok || response.status === 422) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)

        // Update last saved values on successful save
        if (response.ok) {
          this.lastSavedValues.set(rowId, currentValues)
        }
      }

      // Hide spinner on question badge
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.hideQuestionSaving(this.questionIdValue)
      }

      this.savingRows.delete(rowId)
    } catch (error) {
      console.error('Auto-save failed:', error)
      this.savingRows.delete(rowId)

      // Hide spinner on error
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.hideQuestionSaving(this.questionIdValue)
      }
    }
  }
}
